import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useAuthStore } from '@/store/auth';
import { APP_BASE, API_BASE } from '@/lib/api-base';

export function SsoCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('access_token');
    if (!token) {
      setError('No access token received from SSO.');
      return;
    }

    let cancelled = false;

    fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as { detail?: string }).detail || `HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((user) => {
        if (cancelled) return;
        setSession(token, user);
        // `setup` is set when this workspace's onboarding never finished and
        // the signed-in user is the one who can finish it. The wizard URL
        // exists nowhere else once the original tab is closed, so signing in
        // is how you get back to it.
        const setup = searchParams.get('setup');
        navigate(
          setup ? `${APP_BASE}/provisioning?request=${setup}` : `${APP_BASE}/`,
          { replace: true },
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setError(`SSO login failed: ${(err as Error).message}`);
      });

    return () => { cancelled = true; };
  }, [searchParams, setSession, navigate]);

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
                 justifyContent: 'center', bgcolor: 'grey.100', p: 2 }}>
        <Box sx={{ maxWidth: 420, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button variant="outlined" onClick={() => navigate(`${APP_BASE}/login`, { replace: true })}>
            Go to Login
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column',
               alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
      <CircularProgress size={32} sx={{ mb: 2 }} />
      <Typography variant="body2" color="text.secondary">
        Signing you in via SSO…
      </Typography>
    </Box>
  );
}
